/**
 * RescheduleAndMessagePanel
 *
 * Three responsibilities in one panel:
 *   1. Edit Appointment — admins/super_admins manually move a repair to a new
 *      date/time/volunteer. Bypasses normal capacity & deadline guards.
 *   2. Send Message    — admin/super_admin (or the assigned volunteer) sends a
 *      free-text email with optional image. Reply-To routes back to the sender.
 *   3. Messages Timeline — read-only thread of every message (auto + manual)
 *      already sent on this request.
 *
 * Backend endpoints:
 *   POST /api/repair-requests/{id}/reschedule
 *   POST /api/repair-requests/{id}/message
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Calendar, Clock, UserCog, MessageSquare, Send, Image as ImageIcon,
  RefreshCcw, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { formatToUKDate } from '../../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RescheduleAndMessagePanel({
  request,
  volunteers,
  currentUser,
  getAuthHeader,
  onChanged,
}) {
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const isAssignedVolunteer = currentUser?.role === 'volunteer'
    && currentUser?.id === request.assigned_to_id;
  const canMessage = isAdmin || isAssignedVolunteer;

  // ── Event settings (drives date + time slot selects) ──
  const [eventSettings, setEventSettings] = useState({ event_date: '', time_slots: [] });
  const [scheduleDates, setScheduleDates] = useState([]);  // future events (date strings)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pub, sched] = await Promise.all([
          axios.get(`${API}/event-settings/public?_ts=${Date.now()}`),
          axios.get(`${API}/event-settings/schedule`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setEventSettings(pub.data || {});
        // Build a list of unique upcoming date display strings (today and future).
        const dates = (sched.data || [])
          .map(e => e.event_date)
          .filter(Boolean);
        const fallback = pub.data?.event_date ? [pub.data.event_date] : [];
        const merged = Array.from(new Set([...(dates || []), ...fallback]));
        setScheduleDates(merged);
      } catch (_) { /* graceful: pickers stay empty */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Reschedule form state ──
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState(request.appointment_date || '');
  const [newTime, setNewTime] = useState(request.appointment_time || '');
  const [newVolunteerId, setNewVolunteerId] = useState(request.assigned_to_id || '');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const dateOptions = useMemo(() => {
    if (scheduleDates.length === 0 && request.appointment_date) {
      return [request.appointment_date];
    }
    return scheduleDates;
  }, [scheduleDates, request.appointment_date]);

  const handleReschedule = async () => {
    if (!newDate && !newTime && newVolunteerId === (request.assigned_to_id || '')) {
      toast.error('Change at least one of: date, time, or volunteer.');
      return;
    }
    setRescheduling(true);
    try {
      const body = { reason: rescheduleReason || null };
      if (newDate) body.appointment_date = newDate;
      if (newTime) body.appointment_time = newTime;
      if (newVolunteerId && newVolunteerId !== request.assigned_to_id) {
        body.assigned_to_id = newVolunteerId;
      }
      const res = await axios.post(
        `${API}/repair-requests/${request.id}/reschedule`,
        body,
        { headers: getAuthHeader() }
      );
      const cust = res.data.customer_notification?.success;
      const vol = res.data.volunteer_notification?.success;
      toast.success(
        `Rescheduled. Customer email ${cust ? 'sent' : 'NOT sent'}; volunteer email ${vol ? 'sent' : 'skipped/failed'}.`
      );
      setRescheduleOpen(false);
      setRescheduleReason('');
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reschedule failed';
      toast.error(detail);
    } finally {
      setRescheduling(false);
    }
  };

  // ── Message form state ──
  const [messageOpen, setMessageOpen] = useState(false);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgContent, setMsgContent] = useState('');
  const [msgImage, setMsgImage] = useState(null);  // base64 data URL
  const [sending, setSending] = useState(false);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error('Image must be 6MB or smaller.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setMsgImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSendMessage = async () => {
    if (!msgContent.trim()) {
      toast.error('Message cannot be empty.');
      return;
    }
    setSending(true);
    try {
      const body = { content: msgContent.trim() };
      if (msgSubject.trim()) body.subject = msgSubject.trim();
      if (msgImage) body.image_url = msgImage;
      await axios.post(
        `${API}/repair-requests/${request.id}/message`,
        body,
        { headers: getAuthHeader() }
      );
      toast.success('Message sent — customer will reply directly to you.');
      setMessageOpen(false);
      setMsgSubject('');
      setMsgContent('');
      setMsgImage(null);
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Send failed';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  // ── Render ──
  const messages = (request.messages || []).slice().reverse();

  return (
    <div className="space-y-6">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        {isAdmin && (
          <Button
            type="button"
            onClick={() => setRescheduleOpen(v => !v)}
            variant="outline"
            className="border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
            data-testid="open-reschedule-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            {rescheduleOpen ? 'Hide reschedule form' : 'Edit Appointment'}
          </Button>
        )}
        {canMessage && (
          <Button
            type="button"
            onClick={() => setMessageOpen(v => !v)}
            className="bg-[#E87B6A] hover:bg-[#d66a59] text-white"
            data-testid="open-message-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            {messageOpen ? 'Hide message form' : 'Send Message to Customer'}
          </Button>
        )}
      </div>

      {/* Reschedule form */}
      {rescheduleOpen && isAdmin && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-3" data-testid="reschedule-form">
          <h4 className="font-semibold text-[#193460] flex items-center">
            <Sparkles className="w-4 h-4 mr-2" />
            Manual Appointment Override
          </h4>
          <p className="text-xs text-gray-600">
            Bypasses the normal 2-day cutoff and slot capacity. Customer + volunteer will be emailed with the change.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs"><Calendar className="w-3 h-3 inline mr-1" />New date</Label>
              <Select value={newDate} onValueChange={setNewDate}>
                <SelectTrigger className="bg-white" data-testid="reschedule-date">
                  <SelectValue placeholder="Choose date" />
                </SelectTrigger>
                <SelectContent>
                  {dateOptions.length === 0 && (
                    <SelectItem value="__none__" disabled>No upcoming events</SelectItem>
                  )}
                  {dateOptions.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs"><Clock className="w-3 h-3 inline mr-1" />New time slot</Label>
              <Select value={newTime} onValueChange={setNewTime}>
                <SelectTrigger className="bg-white" data-testid="reschedule-time">
                  <SelectValue placeholder="Choose slot" />
                </SelectTrigger>
                <SelectContent>
                  {(eventSettings.time_slots || []).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs"><UserCog className="w-3 h-3 inline mr-1" />Assign to volunteer</Label>
              <Select value={newVolunteerId} onValueChange={setNewVolunteerId}>
                <SelectTrigger className="bg-white" data-testid="reschedule-volunteer">
                  <SelectValue placeholder="Choose volunteer" />
                </SelectTrigger>
                <SelectContent>
                  {volunteers.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Reason (optional — included in email + audit log)</Label>
            <Input
              value={rescheduleReason}
              onChange={e => setRescheduleReason(e.target.value)}
              placeholder="e.g. Customer asked for a later slot"
              className="bg-white"
              data-testid="reschedule-reason"
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleReschedule}
              disabled={rescheduling}
              className="bg-[#193460] hover:bg-[#0f1f3d] text-white"
              data-testid="confirm-reschedule-btn"
            >
              {rescheduling ? 'Saving…' : 'Confirm reschedule & notify'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRescheduleOpen(false)}
              disabled={rescheduling}
            >
              <X className="w-4 h-4 mr-1" />Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Message form */}
      {messageOpen && canMessage && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 space-y-3" data-testid="message-form">
          <h4 className="font-semibold text-[#193460] flex items-center">
            <MessageSquare className="w-4 h-4 mr-2" />
            Send message to {request.customer_name || 'customer'}
          </h4>
          <p className="text-xs text-gray-600">
            Email goes to <strong>{request.customer_email}</strong>. They can reply directly to you ({currentUser?.email}).
          </p>

          <div>
            <Label className="text-xs">Subject (optional)</Label>
            <Input
              value={msgSubject}
              onChange={e => setMsgSubject(e.target.value)}
              placeholder={`Message about your ${request.item_name || 'repair'}`}
              className="bg-white"
              data-testid="message-subject"
            />
          </div>

          <div>
            <Label className="text-xs">Message *</Label>
            <Textarea
              value={msgContent}
              onChange={e => setMsgContent(e.target.value)}
              rows={5}
              placeholder="Type your message…"
              className="bg-white"
              data-testid="message-content"
            />
            <div className="text-xs text-gray-500 mt-1">{msgContent.length} / 5000</div>
          </div>

          <div>
            <Label className="text-xs"><ImageIcon className="w-3 h-3 inline mr-1" />Attach image (optional, ≤6MB)</Label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImagePick}
              className="block text-sm mt-1"
              data-testid="message-image-input"
            />
            {msgImage && (
              <div className="mt-2 relative inline-block">
                <img src={msgImage} alt="attachment preview" className="max-h-40 rounded border" />
                <button
                  type="button"
                  onClick={() => setMsgImage(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                  aria-label="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleSendMessage}
              disabled={sending || !msgContent.trim()}
              className="bg-[#E87B6A] hover:bg-[#d66a59] text-white"
              data-testid="confirm-send-message-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending…' : 'Send message'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMessageOpen(false)}
              disabled={sending}
            >
              <X className="w-4 h-4 mr-1" />Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Messages timeline */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="messages-timeline">
        <h4 className="font-semibold text-[#193460] mb-3 flex items-center">
          <MessageSquare className="w-4 h-4 mr-2" />
          Communication thread <span className="ml-2 text-xs text-gray-500">({messages.length})</span>
        </h4>
        {messages.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No messages sent yet on this request.</p>
        ) : (
          <ul className="space-y-3">
            {messages.map(m => (
              <li
                key={m.id}
                className={`border-l-4 pl-3 py-2 rounded-r-md ${
                  m.is_system_generated
                    ? 'border-gray-400 bg-white'
                    : 'border-[#E87B6A] bg-white'
                }`}
                data-testid={`message-${m.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-[#193460]">{m.sender_name}</span>
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {m.is_system_generated ? 'System' : m.sender_role}
                    </Badge>
                    {m.email_sent ? (
                      <Badge className="text-[10px] bg-green-100 text-green-800 border-green-200">Email sent</Badge>
                    ) : (
                      <Badge className="text-[10px] bg-yellow-100 text-yellow-800 border-yellow-200">No email</Badge>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{formatToUKDate(m.created_at)}</span>
                </div>
                {m.subject && (
                  <p className="text-xs text-gray-500 mt-1 font-medium">Subject: {m.subject}</p>
                )}
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{m.content}</p>
                {m.image_url && (
                  <img src={m.image_url} alt="attachment" className="mt-2 max-h-40 rounded border" />
                )}
                {m.error && (
                  <p className="text-xs text-red-600 mt-1">Delivery error: {m.error}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
